import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Printer, RefreshCcw, Send } from "lucide-react";
import { messagesApi, paymentsApi, type InvoicePrint, type Message, type MessageKind, type MessageSettings, type StatementDocData, type DocView, type Template } from "../lib/api";
import { useCompany } from "../masters/reference";
import { errText, useLoad } from "../ui/useLoad";
import { ErrorNote, Loading, PageHead } from "../ui/bits";
import { InvoiceDoc } from "../vouchers/InvoicePrint";
import { StatementDoc } from "./StatementDoc";
import { SendDialog, type SendTarget } from "./SendDialog";
import { toUrl } from "../shell/routes";
import { fmtDate } from "../companies/CompanyPicker";

const KIND_LABEL: Record<MessageKind, string> = { invoice: "Invoice", credit_note: "Credit note", quote: "Quote", proforma: "Proforma invoice",
  sales_order: "Sales order", purchase_order: "Purchase order", challan: "Delivery challan", statement: "Statement", reminder: "Payment reminder",
  receipt: "Payment thank-you", portal: "Portal invitation" };
const PROVIDERS: [("gmail" | "graph" | "zoho_mail"), string][] = [["gmail", "Gmail / Google Workspace"], ["graph", "Microsoft 365 / Outlook"], ["zoho_mail", "Zoho Mail"]];
const when = (iso?: string | null) => (iso ? new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" }) : "");

// ---------- ST05 Settings › Email and WhatsApp ----------
export function EmailSettings() {
  const { company } = useCompany();
  const { data, error, setData } = useLoad(() => messagesApi.settings(company.id), [company.id]);
  const [s, setS] = useState<MessageSettings | null>(null);
  const [kind, setKind] = useState<MessageKind>("invoice");
  const [busy, setBusy] = useState(false); const [err, setErr] = useState<string | null>(null); const [msg, setMsg] = useState<string | null>(null);
  const [days, setDays] = useState("");
  useEffect(() => { if (data) { setS(data); setDays(data.reminders.days.join(", ")); } }, [data]);
  if (!s) return <section className="page"><PageHead crumb="Settings" title="Email and WhatsApp" code="ST05" />{error ? <ErrorNote text={error.message} /> : <Loading />}</section>;
  const can = s.can.edit;
  const t = s.templates[kind];
  const setT = (p: Partial<Template>) => setS({ ...s, templates: { ...s.templates, [kind]: { ...t, ...p } } });
  const save = async () => {
    setBusy(true); setErr(null); setMsg(null);
    try {
      const dd = days.split(/[,\s]+/).filter(Boolean).map(Number);
      const r = await messagesApi.saveSettings(company.id, { ...s, reminders: { ...s.reminders, days: dd } });
      setData(r); setS(r); setMsg("Saved.");
    } catch (x) { setErr(errText(x)); } finally { setBusy(false); }
  };
  const connect = async (p: string) => {
    setErr(null);
    try { const r = await messagesApi.connectStart(company.id, p, `${window.location.origin}/settings/email/callback`); window.location.assign(r.url); }
    catch (x) { setErr(errText(x)); }
  };
  const off = (k: keyof MessageSettings["services"]) => !s.services[k];
  return (
    <section className="page">
      <PageHead crumb="Settings" title="Email and WhatsApp" code="ST05">
        {can && <button className="primary" disabled={busy} onClick={() => void save()}>{busy ? "Saving…" : "Save"}</button>}
      </PageHead>
      <ErrorNote text={err} />{msg && <p className="msg ok" role="status">{msg}</p>}
      <section className="card panel settings-box">
        <h2>Your mailbox</h2>
        {s.mailbox ? (<>
          <p>Documents go from <b>{s.mailbox.address}</b> ({PROVIDERS.find((p) => p[0] === s.mailbox!.provider)?.[1]}), and land in its Sent folder.
            Connected {fmtDate(s.mailbox.connected_at.slice(0, 10))}{s.mailbox.connected_by ? ` by ${s.mailbox.connected_by}` : ""}.</p>
          {s.mailbox.problem && <p className="msg err">{s.mailbox.problem}</p>}
          {can && <div className="actions"><button onClick={() => void connect(s.mailbox!.provider)}>Connect again</button>
            <button className="link" onClick={() => void messagesApi.disconnect(company.id).then((r) => { setData(r); setS(r); })}>Disconnect</button></div>}
        </>) : (<>
          <p className="small muted">Connect the company's own mailbox so invoices come from your address and sit in your Sent folder.
            {s.services.zeptomail ? " Until then they go from Altekore's sending service in your company's name, with replies to you." : " Until one is connected, emails can't be delivered."}</p>
          {can && <div className="actions">{PROVIDERS.map(([k, label]) => (
            <button key={k} disabled={off(k)} title={off(k) ? "Not set up on Altekore yet" : ""} onClick={() => void connect(k)}>{label}{off(k) ? " (soon)" : ""}</button>))}</div>}
        </>)}
        <div className="fields two">
          <label>Sender name<input value={s.from_name} maxLength={80} disabled={!can} onChange={(e) => setS({ ...s, from_name: e.target.value })} /></label>
          <label>Replies go to<input value={s.reply_to ?? ""} placeholder="The person who sends it" disabled={!can} onChange={(e) => setS({ ...s, reply_to: e.target.value })} /></label>
        </div>
        <label className="check"><input type="checkbox" checked={s.copy_me} disabled={!can} onChange={(e) => setS({ ...s, copy_me: e.target.checked })} /> Copy me on every email I send</label>
      </section>
      <section className="card panel settings-box">
        <h2>WhatsApp</h2>
        <p className="small muted">{s.services.whatsapp ? "Documents can go by WhatsApp from Altekore's business number, as an approved message with a button that opens the document."
          : "WhatsApp isn't set up on Altekore yet. Once it is, a WhatsApp button appears next to email when you send."}</p>
        <label className="check"><input type="checkbox" checked={s.whatsapp} disabled={!can} onChange={(e) => setS({ ...s, whatsapp: e.target.checked })} /> Allow sending by WhatsApp</label>
      </section>
      <section className="card panel settings-box">
        <h2>Payment reminders</h2>
        <label className="check"><input type="checkbox" checked={s.reminders.active} disabled={!can} onChange={(e) => setS({ ...s, reminders: { ...s.reminders, active: e.target.checked } })} /> Remind customers of unpaid invoices automatically</label>
        <div className="fields two">
          <label>Days from the due date <span className="muted small">(−3 = three days before; 0 = on the day)</span>
            <input value={days} disabled={!can} onChange={(e) => setDays(e.target.value)} /></label>
          <div><div className="label">By</div>
            {(["email", "whatsapp"] as const).map((ch) => <label key={ch} className="check small"><input type="checkbox" disabled={!can} checked={s.reminders.channels.includes(ch)}
              onChange={() => setS({ ...s, reminders: { ...s.reminders, channels: s.reminders.channels.includes(ch) ? s.reminders.channels.filter((x) => x !== ch) : [...s.reminders.channels, ch] } })} /> {ch === "email" ? "Email" : "WhatsApp"}</label>)}</div>
        </div>
        <p className="small muted">Sent each morning from 9 a.m. to customers with an email or mobile number, once per invoice per day listed.{s.reminders.ran_on ? ` Last run ${fmtDate(s.reminders.ran_on)}.` : ""}</p>
        <RemindersToday companyId={company.id} canSend={s.can.send} />
      </section>
      <section className="card panel settings-box">
        <h2>Wording</h2>
        <p className="small muted">{"{party}"}, {"{number}"}, {"{date}"}, {"{amount}"}, {"{due_date}"}, {"{company}"} and {"{link}"} are filled in for each message; reminders also have {"{when}"} and {"{due}"}.</p>
        <div className="chips">{(Object.keys(KIND_LABEL) as MessageKind[]).map((k) => <button key={k} type="button" className={`chip ${k === kind ? "on" : ""}`} onClick={() => setKind(k)}>{KIND_LABEL[k]}{s.templates[k].own ? " •" : ""}</button>)}</div>
        <label>Subject<input value={t.subject} disabled={!can} maxLength={200} onChange={(e) => setT({ subject: e.target.value })} /></label>
        <label>Message<textarea rows={8} value={t.body} disabled={!can} onChange={(e) => setT({ body: e.target.value })} /></label>
      </section>
    </section>
  );
}

function RemindersToday({ companyId, canSend }: { companyId: string; canSend: boolean }) {
  const { data, reload } = useLoad(() => messagesApi.remindersDue(companyId), [companyId]);
  const [busy, setBusy] = useState(false); const [msg, setMsg] = useState<string | null>(null);
  if (!data || data.length === 0) return <p className="small muted">No reminders due today.</p>;
  return (
    <div>
      <p className="small"><b>Due today:</b> {data.map((r) => `${r.number} to ${r.party} (${r.channel})`).join("; ")}</p>
      {canSend && <button disabled={busy} onClick={() => { setBusy(true); messagesApi.runReminders(companyId).then((r) => { setMsg(`${r.sent} sent.`); reload(); }).catch((e) => setMsg(errText(e))).finally(() => setBusy(false)); }}>
        Send them now</button>}{msg && <span className="small"> {msg}</span>}
    </div>
  );
}

// the mailbox's sign-in comes back here with a code
export function MailCallback() {
  const { company } = useCompany();
  const [q] = useSearchParams();
  const nav = useNavigate();
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    const state = q.get("state"), code = q.get("code");
    if (q.get("error")) { setErr(q.get("error_description") ?? q.get("error")); return; }
    if (!state || !code) { setErr("The sign-in didn't come back with access."); return; }
    messagesApi.connectFinish(company.id, state, code).then(() => nav(toUrl("settings/email"), { replace: true })).catch((e) => setErr(errText(e)));
  }, [company.id, q, nav]);
  return <section className="page"><PageHead crumb="Settings" title="Connecting your mailbox" />{err ? <><ErrorNote text={err} /><Link to={toUrl("settings/email")}>Back</Link></> : <Loading />}</section>;
}

// ---------- MS10 the sending log ----------
export function SentMessages() {
  const { company } = useCompany();
  const [status, setStatus] = useState("");
  const { data, error, reload } = useLoad(() => messagesApi.list(company.id, status ? { status } : {}), [company.id, status]);
  const [busy, setBusy] = useState<string | null>(null);
  return (
    <section className="page">
      <PageHead crumb="Sales" title="Sent messages" code="MS10" />
      <div className="toolbar"><label className="inline-label">Show<select value={status} onChange={(e) => setStatus(e.target.value)}>
        <option value="">Everything</option><option value="failed">Not delivered</option><option value="sent">Sent</option></select></label>
        <Link to={toUrl("settings/email")} className="small">Email and WhatsApp settings</Link></div>
      <ErrorNote text={error?.message} />
      {!data ? <Loading /> : data.length === 0 ? <p className="muted">Nothing sent yet. Send an invoice from its page, or a statement from a customer's page.</p> : (
        <table className="grid-table">
          <thead><tr><th>When</th><th>What</th><th>To</th><th>By</th><th>Status</th><th /></tr></thead>
          <tbody>{data.map((m) => (
            <tr key={m.id}>
              <td className="small">{when(m.at)}</td>
              <td>{KIND_LABEL[m.kind] ?? m.kind} {m.voucher_id ? <Link to={toUrl(`vouchers/v/${m.voucher_id}`)} className="mono">{m.document}</Link> : <span className="mono">{m.document}</span>}
                <div className="small muted">{m.party}{m.attachment ? ` · ${m.attachment}` : ""}</div></td>
              <td className="small">{m.channel === "whatsapp" ? "WhatsApp " : ""}{m.to.join(", ")}</td>
              <td className="small">{m.by}</td>
              <td><MessageState m={m} /></td>
              <td>{m.status === "failed" && <button className="link" disabled={!!busy} onClick={() => { setBusy(m.id); messagesApi.retry(company.id, m.id).then(reload).catch(() => reload()).finally(() => setBusy(null)); }}>
                <RefreshCcw size={13} /> {busy === m.id ? "Sending…" : "Send again"}</button>}</td>
            </tr>))}</tbody>
        </table>)}
    </section>
  );
}

export function MessageState({ m }: { m: Message }) {
  if (m.status === "failed") return <span className="pill bad" title={m.error}>Not delivered{m.retry_at ? ", trying again" : ""}<div className="small">{m.error}</div></span>;
  if (m.viewed_at) return <span className="pill ok">Opened {when(m.viewed_at)}</span>;
  return <span className="pill">Sent {when(m.sent_at)}{m.via?.includes("test") ? " (test)" : ""}</span>;
}

// the messages about one document, with a Send button: on invoice, order and party pages
export function DocMessages({ companyId, target, label = "Send", autoOpen = false }: { companyId: string; target: SendTarget; label?: string; autoOpen?: boolean }) {
  const [open, setOpen] = useState(autoOpen);
  const f = useMemo(() => target.kind === "statement" ? { party_id: target.party_id, kind: "statement" } : { voucher_id: target.voucher_id, order_id: target.order_id }, [target]);
  const { data, reload } = useLoad(() => messagesApi.list(companyId, { ...f, limit: 5 }), [companyId, f]);
  return (
    <span className="doc-messages">
      <button onClick={() => setOpen(true)}><Send size={14} aria-hidden="true" /> {label}</button>
      {data && data.length > 0 && <span className="small muted"> Last: {data[0].channel === "whatsapp" ? "WhatsApp" : "email"} to {data[0].to[0]} · <MessageState m={data[0]} /></span>}
      {open && <SendDialog companyId={companyId} target={target} onClose={() => { setOpen(false); reload(); }} onSent={() => reload()} />}
    </span>
  );
}

// ---------- a customer's or vendor's statement ----------
export function StatementPage({ side }: { side: "customer" | "supplier" }) {
  const { company, ref } = useCompany();
  const { partyId } = useParams();
  const [q, setQ] = useSearchParams();
  const yearStart = ref?.opening_year?.starts_on ?? `${new Date().getFullYear()}-04-01`;
  const from = q.get("from") ?? yearStart, to = q.get("to") ?? new Date().toISOString().slice(0, 10);
  const { data, error } = useLoad(() => messagesApi.statement(company.id, partyId!, from, to), [company.id, partyId, from, to]);
  const target = useMemo<SendTarget>(() => ({ kind: "statement", party_id: partyId, from, to }), [partyId, from, to]);
  return (
    <section className="page doc-page">
      <div className="doc-tools no-print">
        <Link to={toUrl(`parties/${side === "customer" ? "customers" : "vendors"}/${partyId}`)}>← Back</Link>
        <label className="inline-label">From<input type="date" value={from} onChange={(e) => setQ({ from: e.target.value, to })} /></label>
        <label className="inline-label">To<input type="date" value={to} onChange={(e) => setQ({ from, to: e.target.value })} /></label>
        <button onClick={() => window.print()}><Printer size={15} aria-hidden="true" /> Print or save as PDF</button>
        <DocMessages companyId={company.id} target={target} label="Send statement" />
      </div>
      <ErrorNote text={error?.message} />
      {data ? <StatementDoc d={data} /> : !error && <Loading />}
    </section>
  );
}

// ---------- an order, quote, proforma or challan as a document ----------
export function OrderPrintPage({ back }: { back: string }) {
  const { company } = useCompany();
  const { orderId } = useParams();
  const [d, setD] = useState<InvoicePrint | null>(null); const [err, setErr] = useState<string | null>(null);
  useEffect(() => { messagesApi.orderPrint(company.id, orderId!).then(setD).catch((e) => setErr(errText(e))); }, [company.id, orderId]);
  const target = useMemo<SendTarget>(() => ({ order_id: orderId }), [orderId]);
  if (err) return <section className="page"><ErrorNote text={err} /></section>;
  if (!d) return <section className="page"><Loading /></section>;
  return (
    <section className="page doc-page">
      <div className="doc-tools no-print">
        <Link to={toUrl(`${back}/${orderId}`)}>← Back</Link>
        <button onClick={() => window.print()}><Printer size={15} aria-hidden="true" /> Print or save as PDF</button>
        <DocMessages companyId={company.id} target={target} />
        <Link className="small" to={toUrl("company/documents")}>Change the look</Link>
      </div>
      <InvoiceDoc d={d} />
    </section>
  );
}

// ---------- /d/<token>: the document behind a link, for anyone who has it ----------
export function PublicDoc() {
  const { token } = useParams();
  const [d, setD] = useState<DocView | null>(null); const [err, setErr] = useState<string | null>(null);
  useEffect(() => { messagesApi.view(token!).then(setD).catch((e) => setErr(errText(e))); }, [token]);
  useEffect(() => { if (d) document.title = `${d.kind === "statement" ? "Statement" : (d as InvoicePrint).document.title} · ${d.company_name ?? ""}`; }, [d]);
  if (err) return <main className="public-doc"><div className="card panel"><h1>Can't open this</h1><p>{err}</p></div></main>;
  if (!d) return <main className="public-doc"><p className="muted">Opening…</p></main>;
  const inv = d.kind !== "statement" ? (d as InvoicePrint) : null;
  return (
    <main className="public-doc">
      <div className="doc-tools no-print">
        <b>{d.company_name}</b>
        <button className="primary" onClick={() => window.print()}><Printer size={15} aria-hidden="true" /> Download or print</button>
        {inv?.pay && !inv.pay.paid && inv.pay.link && !inv.pay.test && <a className="button primary" href={inv.pay.link}>Pay {inv.pay.currency === "INR" ? "₹" : inv.pay.currency + " "}{(inv.pay.pending ?? inv.pay.amount).toLocaleString("en-IN", { minimumFractionDigits: 2 })} online</a>}
        {inv?.pay && !inv.pay.paid && inv.pay.test && <button className="primary" onClick={() => paymentsApi.test(token!).then(setD).catch((e) => setErr(errText(e)))}>
          Pay {(inv.pay.pending ?? inv.pay.amount).toLocaleString("en-IN", { minimumFractionDigits: 2 })} (test payment)</button>}
        {inv?.pay?.paid && <span className="pill ok">Paid</span>}
        {inv?.order_id && inv.document.kind === "quote" && <QuoteAccept token={token!} d={inv} onDone={setD} />}
      </div>
      {d.kind === "statement" ? <StatementDoc d={d as StatementDocData} /> : <InvoiceDoc d={inv!} />}
      <p className="small muted no-print center">Sent with Altekore Books</p>
    </main>
  );
}

// accepting a quote from its link (226)
function QuoteAccept({ token, d, onDone }: { token: string; d: InvoicePrint; onDone: (d: DocView) => void }) {
  const [name, setName] = useState(""); const [busy, setBusy] = useState(false); const [err, setErr] = useState<string | null>(null);
  if (d.accepted) return <span className="pill ok">Accepted</span>;
  return (
    <form className="inline-form" onSubmit={(e) => { e.preventDefault(); setBusy(true); messagesApi.acceptQuote(token, name).then(onDone).catch((x) => setErr(errText(x))).finally(() => setBusy(false)); }}>
      <input placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)} required />
      <button className="primary" disabled={busy}>Accept this quote</button>{err && <span className="msg err small">{err}</span>}
    </form>
  );
}
